import { storageKeyWithSuffix } from '@/lib/config/brand'
import { StorageKeys } from '@/lib/storage/keys'

export type SoftBookmarkAudio = {
  seconds: number
  duration: number
  attachmentId: string
  sourceHash: string
  updatedAt: number
}

export type SoftBookmark = {
  /** Top-of-viewport scroll ratio (0-1). */
  ratio: number
  /** Nearest heading id when available. */
  headingId: string | null
  updatedAt: number
  audio?: SoftBookmarkAudio
}

export const SOFT_BOOKMARK_CHANGED_EVENT = 'flownotes-soft-bookmark-changed'

/** Reading-bookmark chrome (gutter + overflow actions). Off until the user opts in. */
export const SOFT_BOOKMARKS_ENABLED_CHANGED_EVENT = 'flownotes-soft-bookmarks-enabled-changed'

export type SoftBookmarkChangedDetail = {
  documentId: string
  bookmark: SoftBookmark | null
}

function parseSoftBookmarkAudio(raw: SoftBookmarkAudio | undefined): SoftBookmarkAudio | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  if (typeof raw.seconds !== 'number' || !Number.isFinite(raw.seconds)) return undefined
  if (typeof raw.attachmentId !== 'string' || !raw.attachmentId) return undefined
  if (typeof raw.sourceHash !== 'string' || !raw.sourceHash) return undefined
  return {
    seconds: Math.max(0, raw.seconds),
    duration: typeof raw.duration === 'number' && Number.isFinite(raw.duration) ? raw.duration : 0,
    attachmentId: raw.attachmentId,
    sourceHash: raw.sourceHash,
    updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : Date.now(),
  }
}

export function setSoftBookmarkAudio(
  documentId: string,
  audio: Omit<SoftBookmarkAudio, 'updatedAt'> & { updatedAt?: number },
): SoftBookmark {
  const existing = getSoftBookmark(documentId)
  return setSoftBookmark(documentId, {
    ratio: existing?.ratio ?? 0,
    headingId: existing?.headingId ?? null,
    audio: {
      seconds: Math.max(0, audio.seconds),
      duration: Math.max(0, audio.duration),
      attachmentId: audio.attachmentId,
      sourceHash: audio.sourceHash,
      updatedAt: audio.updatedAt ?? Date.now(),
    },
  })
}

export function resumeSecondsForAudio(
  bookmark: SoftBookmark | null,
  sourceHash: string | null | undefined,
  attachmentId: string | null | undefined,
): number | null {
  const audio = bookmark?.audio
  if (!audio || !sourceHash || !attachmentId) return null
  if (audio.sourceHash !== sourceHash) return null
  if (audio.attachmentId !== attachmentId) return null
  return audio.seconds
}

function bookmarkKey(documentId: string): string {
  return storageKeyWithSuffix('soft_bookmark', documentId)
}

export function getSoftBookmark(documentId: string | null | undefined): SoftBookmark | null {
  if (!documentId || typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(bookmarkKey(documentId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as SoftBookmark
    if (typeof parsed?.ratio !== 'number' || !Number.isFinite(parsed.ratio)) return null
    return {
      ratio: Math.max(0, Math.min(1, parsed.ratio)),
      headingId: typeof parsed.headingId === 'string' ? parsed.headingId : null,
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
      audio: parseSoftBookmarkAudio(parsed.audio),
    }
  } catch {
    return null
  }
}

function emit(documentId: string, bookmark: SoftBookmark | null): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(SOFT_BOOKMARK_CHANGED_EVENT, {
      detail: { documentId, bookmark } satisfies SoftBookmarkChangedDetail,
    })
  )
}

export function setSoftBookmark(
  documentId: string,
  bookmark: Omit<SoftBookmark, 'updatedAt'> & { updatedAt?: number }
): SoftBookmark {
  const existing = getSoftBookmark(documentId)
  const next: SoftBookmark = {
    ratio: Math.max(0, Math.min(1, bookmark.ratio)),
    headingId: bookmark.headingId ?? null,
    updatedAt: bookmark.updatedAt ?? Date.now(),
    audio: bookmark.audio ?? existing?.audio,
  }
  try {
    localStorage.setItem(bookmarkKey(documentId), JSON.stringify(next))
  } catch {
    // ignore
  }
  emit(documentId, next)
  return next
}

export function clearSoftBookmark(documentId: string): void {
  try {
    localStorage.removeItem(bookmarkKey(documentId))
  } catch {
    // ignore
  }
  emit(documentId, null)
}

function readStoredBoolean(key: string, defaultValue: boolean): boolean {
  if (typeof localStorage === 'undefined') return defaultValue
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return defaultValue
    const parsed = JSON.parse(raw) as { _version?: number; value?: unknown } | boolean
    const value =
      parsed && typeof parsed === 'object' && '_version' in parsed ? parsed.value : parsed
    return typeof value === 'boolean' ? value : defaultValue
  } catch {
    return defaultValue
  }
}

/** Whether heading bookmark controls are visible. Default off (opt-in). */
export function getSoftBookmarksEnabled(): boolean {
  return readStoredBoolean(StorageKeys.EDITOR_SOFT_BOOKMARKS_ENABLED, false)
}

export function setSoftBookmarksEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(
      StorageKeys.EDITOR_SOFT_BOOKMARKS_ENABLED,
      JSON.stringify({ _version: 1, value: enabled })
    )
  } catch {
    // ignore quota / private mode
  }
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(SOFT_BOOKMARKS_ENABLED_CHANGED_EVENT, { detail: { enabled } }))
}

/** Find nearest heading id at or above the given scroll ratio. */
export function nearestHeadingIdForRatio(
  headings: Array<{ id: string; ratio: number }>,
  ratio: number
): string | null {
  if (headings.length === 0) return null
  let best: { id: string; ratio: number } | null = null
  for (const h of headings) {
    if (h.ratio <= ratio + 0.001 && (!best || h.ratio >= best.ratio)) {
      best = h
    }
  }
  return best?.id ?? headings[0]?.id ?? null
}

/** Same ratio space as computeHeadingRatios: offsetTop / scrollHeight. */
export function headingRatiosFromOffsets(
  headings: Array<{ id: string; offsetTop: number }>,
  scrollHeight: number
): Array<{ id: string; ratio: number }> {
  const total = scrollHeight > 0 ? scrollHeight : 1
  return headings
    .filter((h) => h.id)
    .map((h) => ({ id: h.id, ratio: h.offsetTop / total }))
}
